import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import type { VerificationChallenge as Challenge, VerificationMethod } from '@tenfour/contracts';
import { verificationApi } from '@/lib/verification';
import s from './verification.module.css';
import type { AuthUser } from './AuthContext';

export interface VerificationResult {
  managementToken?: string;
  enabled?: boolean;
  recoveryCodes?: string[];
  token?: string;
  accessToken?: string;
  user?: AuthUser;
}

export function VerificationChallenge({ initial, onVerified, onCancel, admin = false, enrollment = false }: {
  initial: Challenge; onVerified: (result: VerificationResult) => void | Promise<void>; onCancel: () => void; admin?: boolean; enrollment?: boolean;
}) {
  const [challenge, setChallenge] = useState(initial);
  const [code, setCode] = useState('');
  const [backup, setBackup] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const errorId = useId();
  const [resendAt, setResendAt] = useState(Date.now() + initial.resendAfterSeconds * 1000);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const timer = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(timer); }, []);
  const wait = Math.max(0, Math.ceil((resendAt - now) / 1000));

  async function send(method: VerificationMethod) {
    setBusy(true); setError(''); setCode(''); setBackup(false);
    try {
      const next = await verificationApi<Challenge>('challenge', { challengeToken: challenge.challengeToken, method }, admin);
      setChallenge(next); setResendAt(Date.now() + next.resendAfterSeconds * 1000);
    } catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  }
  async function submit(event: FormEvent) {
    event.preventDefault(); setBusy(true); setError('');
    try {
      const result = await verificationApi<VerificationResult>('verify', { challengeToken: challenge.challengeToken, code, recovery: backup }, admin);
      await onVerified(result);
    } catch (err) { setError((err as Error).message); setCode(''); }
    finally { setBusy(false); }
  }

  return <div className={s.challenge}>
    <button type="button" className={s.back} onClick={onCancel} aria-label="Back">←</button>
    <h2>{backup ? 'Enter a recovery code' : !challenge.channel ? 'Choose how to verify' : challenge.channel === 'totp' ? 'Enter your authenticator code' : 'Enter the code we sent you'}</h2>
    {challenge.destination && !backup && <p>We sent it to {challenge.destination}</p>}
    {challenge.channel === 'totp' && !backup && <p>Use the six-digit code from your authenticator app.</p>}
    {!challenge.channel && !backup ? <div className={s.methods}>
      {challenge.methods.map((method) => <button type="button" className={s.method} key={method.id} onClick={() => send(method.id)} disabled={busy}>
        <strong>{method.label}</strong>{method.destination && <span>{method.destination}</span>}
      </button>)}
    </div> : <form onSubmit={submit} className={s.form}>
      {backup ? <input value={code} onChange={(event) => setCode(event.target.value)} autoComplete="off" aria-label="Recovery code" aria-invalid={!!error || undefined} aria-describedby={error ? errorId : undefined} className={s.backup} /> : <CodeInput value={code} onChange={setCode} disabled={busy} errorId={error ? errorId : undefined} />}
      {error && <p id={errorId} role="alert" className={s.error}>{error}</p>}
      {!backup && challenge.channel !== 'totp' && <p className={s.resend}>Didn’t receive a code? <button type="button" onClick={() => send(challenge.channel!)} disabled={busy || wait > 0}>{wait ? `Send again in ${wait}s` : 'Send again'}</button></p>}
      <button type="submit" className={s.continue} disabled={busy || (backup ? !code.trim() : code.length !== 6)}>{busy ? 'Verifying…' : 'Continue'}</button>
    </form>}
    {!challenge.channel && error && <p role="alert" className={s.error}>{error}</p>}
    {!enrollment && <div className={s.alternatives}>
      {(challenge.channel || backup) && challenge.methods.length > 1 && <button type="button" onClick={() => { setChallenge({ ...challenge, channel: undefined, destination: undefined }); setBackup(false); setCode(''); setError(''); }}>Use another method</button>}
      <button type="button" onClick={() => { setBackup(!backup); setCode(''); setError(''); }}>{backup ? 'Use a verification code' : 'Use a recovery code'}</button>
    </div>}
  </div>;
}

export function CodeInput({ value, onChange, disabled, errorId }: { value: string; onChange: (value: string) => void; disabled?: boolean; errorId?: string }) {
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const [pointer, setPointer] = useState(false);
  function write(index: number, raw: string) {
    const digits = raw.replace(/\D/g, '');
    if (!digits) { onChange(value.slice(0, index)); return; }
    const position = Math.min(index, value.length);
    const next = (value.slice(0, position) + digits + value.slice(position + digits.length)).slice(0, 6);
    onChange(next);
    inputs.current[Math.min(position + digits.length, 5)]?.focus();
  }
  return <div className={s.digits} role="group" aria-label="Six-digit verification code" data-pointer-focus={pointer} onPointerDown={() => setPointer(true)} onKeyDown={() => setPointer(false)}>
    {Array.from({ length: 6 }, (_, index) => <input key={index} ref={(node) => { inputs.current[index] = node; }}
      data-verification-digit aria-label={`Digit ${index + 1}`} value={value[index] || ''} inputMode="numeric" pattern="[0-9]*" type="text" autoComplete={index === 0 ? 'one-time-code' : 'off'}
      maxLength={6} disabled={disabled} aria-invalid={!!errorId || undefined} aria-describedby={errorId} onChange={(event) => write(index, event.target.value)}
      onFocus={(event) => { if (index > value.length) inputs.current[value.length]?.focus(); else event.target.select(); }}
      onPaste={(event) => { event.preventDefault(); write(0, event.clipboardData.getData('text')); }}
      onKeyDown={(event) => {
        if (event.key === 'Backspace' && !value[index] && index > 0) { event.preventDefault(); onChange(value.slice(0, index - 1)); inputs.current[index - 1]?.focus(); }
        if (event.key === 'ArrowLeft' && index > 0) inputs.current[index - 1]?.focus();
        if (event.key === 'ArrowRight' && index < 5) inputs.current[index + 1]?.focus();
      }} />)}
  </div>;
}
